/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, signal} from "@angular/core";
import * as _ from "lodash";

import {LocalStorage} from "./local-storage";
import {LocalUserPermissions} from "./local-user-permissions";
import {LocaleHelper} from "../util/locale-helper";

type Profile = {
  userId: string;
  username: string;
  name: string;
  surname: string;
  displayName: string;
  locale: string;
  canMasquerade: boolean,
  twoFactorAuthType: boolean,
  login2faRequired: boolean,
  networkTagIds: any;
  defaultNetworkTagId: string;
  preferredCurrency: string;
  supportUserId: string;
  app: string;
  passwordExpiry: Date;
  passwordExpiresDays: number;
  profile: any;
};

const CALT_TAG_ID = 'b7462';
export const LEGACY_USER_PROFILE_KEYS = ['mauiHistory', 'mauiPrefs', 'studio'];

export function removeLegacyUserProfileFields(profile: any): boolean {
  if (!_.isObject(profile)) {
    return false;
  }
  let changed = false;
  _.forEach(LEGACY_USER_PROFILE_KEYS, (key) => {
    if (_.has(profile, key)) {
      delete profile[key];
      changed = true;
    }
  });
  return changed;
}

function getDefaultNetworkTag(networkTagIds: any) {
  if (!_.isArray(networkTagIds)) {
    return null;
  }
  if (networkTagIds.indexOf(CALT_TAG_ID) > -1) {
    return CALT_TAG_ID;
  }
  return _.head(networkTagIds);
}

@Injectable({
  providedIn: 'root'
})
export class LocalUserProfile {
  private static readonly USER_PROFILE_STORAGE_KEY = 'user.profile';
  private static readonly LOCAL_ONLY_PROFILE_USERS = ['adnuntius@adnuntius.com'];
  private static readonly APPS_INFO = {
    ADVERTISING: {
      id: 'ADVERTISING'
    },
    DATA: {
      id: 'DATA'
    },
    POLITICAL: {
      id: 'POLITICAL'
    }
  };

  private subs: Array<{key: any; func: (updatedObj: any) => void}> = [];
  private readonly revision = signal(0);
  profile: Profile;

  constructor(private readonly localUserPermissions: LocalUserPermissions) {
  }

  private static initDefaultProfile(id, username, name, surname, displayName, locale, canMasquerade, twoFactorAuthType, login2faRequired, networkTagIds, preferredCurrency, passwordExpiry, passwordExpiresDays, supportUserId, app): Profile {
    const updatedLocale = locale || LocaleHelper.getNavigatorLanguage(window);
    return {
      userId: id,
      username: username,
      name: name,
      surname: surname,
      displayName: displayName,
      canMasquerade: canMasquerade,
      twoFactorAuthType: twoFactorAuthType,
      login2faRequired: login2faRequired,
      networkTagIds: networkTagIds,
      locale: updatedLocale,
      preferredCurrency: preferredCurrency,
      passwordExpiry: passwordExpiry,
      passwordExpiresDays: passwordExpiresDays,
      defaultNetworkTagId: getDefaultNetworkTag(networkTagIds),
      supportUserId: supportUserId,
      app: app || 'ADVERTISING',
      profile: {
        adnLists: {},
        adnPrefs: {
          showReleaseHighlight: 'SHOW',
          timeZone: 'UTC',
          defaultPageSize: 25,
          locale: updatedLocale,
          modalCtrl: false
        }
      }
    };
  }

  // Used by auth after mutating fields on the object returned from create().
  save() {
    removeLegacyUserProfileFields(this.profile?.profile);
    LocalStorage.obtain().setItem(LocalUserProfile.USER_PROFILE_STORAGE_KEY, this.profile);
    this.revision.update((value) => value + 1);
  }

  private refresh() {
    const localStorageInfo = LocalStorage.obtain().getItem(LocalUserProfile.USER_PROFILE_STORAGE_KEY);
    this.profile = _.isEmpty(localStorageInfo) ? _.isEmpty(this.profile) ? {} : this.profile : localStorageInfo;
    if (removeLegacyUserProfileFields(this.profile?.profile)) {
      this.save();
    }
    if (!_.isEmpty(this.profile) && _.isArray(this.profile.networkTagIds) && (!this.profile.defaultNetworkTagId || this.profile.networkTagIds.indexOf(this.profile.defaultNetworkTagId) < 0)) {
      const networkWithMostTeams = this.localUserPermissions.getNetworkWithMostTeams(this.profile.networkTagIds);
      this.profile.defaultNetworkTagId = networkWithMostTeams || getDefaultNetworkTag(this.profile.networkTagIds);
    }
  }

  create(user, networks, profile, supportUserId, app?) {
    const storedProfile = LocalStorage.obtain().getItem(LocalUserProfile.USER_PROFILE_STORAGE_KEY);
    this.profile = LocalUserProfile.initDefaultProfile(user.id, user.username, user.name, user.surname, user.displayName, user.locale, user.canMasquerade, user.twoFactorAuthType, user.login2faRequired, networks, user.preferredCurrency, user.passwordExpiry, user.passwordExpiresDays, supportUserId, app);
    _.merge(this.profile.profile, profile);
    if (this.isLocalOnlyProfileUser(user.username) && storedProfile?.username === user.username) {
      _.merge(this.profile.profile, storedProfile.profile);
    }
    this.save();
    return this.profile;
  }

  destroy() {
    this.profile = null;
    const removed = LocalStorage.obtain().remove(LocalUserProfile.USER_PROFILE_STORAGE_KEY);
    this.revision.update((value) => value + 1);
    return removed;
  }

  get(prop?): any {
    this.revision();
    this.refresh();
    return prop ? _.get(this.profile, prop) : this.profile;
  }

  getProfile() {
    return this.get('profile');
  }

  getCurrentApp() {
    const currentApp: string = this.get('app');
    const availableApps = this.localUserPermissions.getApps();
    if (availableApps.length > 0 && availableApps.indexOf(currentApp) < 0) {
      if (availableApps.indexOf(LocalUserProfile.APPS_INFO.ADVERTISING.id) > -1) {
        return LocalUserProfile.APPS_INFO.ADVERTISING.id;
      }
      return availableApps[0];
    }
    if (availableApps.indexOf(currentApp) > -1 && LocalUserProfile.APPS_INFO[currentApp]) {
      return currentApp;
    }
    return LocalUserProfile.APPS_INFO.ADVERTISING.id;
  }

  set(prop, value) {
    this.refresh();
    _.set(this.profile, prop, value);
    this.save();
    this.refresh();
  }

  getPreferredCurrency(): string {
    return _.get(this.get(), 'preferredCurrency') || _.get(this.get(), ['profile', 'adnPrefs', 'preferredCurrency']);
  }

  safeSaveProfile(fields, data): any {
    this.refresh();
    _.set(this.profile.profile, fields, data);
    this.save();
    this.refresh();
    return this.profile;
  }

  saveProfile(profile): any {
    this.refresh();
    this.profile.profile = _.merge({}, this.profile.profile, profile);
    this.save();
    this.refresh();
    return this.profile;
  }

  isLocalOnlyProfileUser(username?): boolean {
    const checkedUsername = username || this.get('username');
    return _.includes(LocalUserProfile.LOCAL_ONLY_PROFILE_USERS, checkedUsername);
  }

  isSameUser(uProfile: any): boolean {
    this.refresh();
    return _.get(this.profile, 'userId') && _.get(this.profile, 'userId') === _.get(uProfile, 'userId');
  }

  canSeeReachCharts(): boolean {
    return false;
    // return _.includes(["adnuntius@adnuntius.com", "broker1@adnuntius.com", "richard@adnuntius.com"], this.get("username"));
  }

  isSpecialUser(): boolean {
    return _.includes(["adnuntius@adnuntius.com", "broker1@adnuntius.com"], this.get("username"));
  }

  canPauseAdUnits(): boolean {
    return _.includes(["adnuntius@adnuntius.com", "broker1@adnuntius.com"], this.get("username"));
  }

  canLimitBurnRates(): boolean {
    return _.includes(["adnuntius@adnuntius.com", "broker1@adnuntius.com"], this.get("username"));
  }

  canSeeTestElections(): boolean {
    return _.includes(["adnuntius@adnuntius.com", "broker1@adnuntius.com"], this.get("username"));
  }

  canSwitchToMaui(): boolean {
    return _.includes(["dirk@adnuntius.com", "adnuntius@adnuntius.com", "broker1@adnuntius.com", "henrik@adnuntius.com", "dennis@adnuntius.com", "lasse@adnuntius.com", "micke@adnuntius.com", "stian@adnuntius.com"], this.get("username"));
  }

  isReadOnly(): boolean {
    return _.includes(["reg@hasv.no"], this.get("supportUserId"));
  }

  isMasquerading(): boolean {
    return this.get("supportUserId") !== null;
  }

  hasNetwork(networkTagId) {
    this.refresh();
    return _.includes(this.profile.networkTagIds, networkTagId);
  }

  updateDefaultNetworkId(networkTagId) {
    this.refresh();
    if (networkTagId && _.indexOf(this.profile.networkTagIds, networkTagId) > -1) {
      this.profile.defaultNetworkTagId = networkTagId;
      this.revision.update((value) => value + 1);
    }
  }

  subscribe(key: any, func: (updatedObj: any) => void): () => void {
    if (!_.isFunction(func)) {
      return () => undefined;
    }
    const subscription = {key: key, func: func};
    this.subs.push(subscription);
    return () => {
      _.pull(this.subs, subscription);
    };
  }

  publish(key, updatedObj) {
    this.revision.update((value) => value + 1);
    _.forEach(this.subs, (sub) => {
      if (sub.key === key) {
        sub.func(updatedObj);
      }
    });
  }
}
